import { motion } from 'framer-motion';
import { Gift, FileText, Calculator, Users, BookOpen, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';

export const WhatYouGet = () => {
  const benefits = [
    {
      icon: Gift,
      title: '🎁 1 Entry into the $1,000 Visa® Holiday Gift Card Giveaway',
      text: 'One lucky winner will receive a $1,000 prepaid Visa gift card to use toward gifts, travel, bills, or anything you choose.'
    },
    {
      icon: FileText,
      title: '📄 1-Page Holiday Money-Saver Kit (PDF)',
      text: 'A simple, practical checklist from our CPA team to help you spot common year-end tax leaks and avoid costly surprises.'
    },
    {
      icon: Calculator,
      title: '🧮 Priority Access to Our 2025 Tax Estimator Tool',
      text: "After you enter, we'll send you a link to explore LetsTalkTaxes.io, where you can estimate your 2025 tax liability and see how new tax law changes may affect your bottom line."
    },
    {
      icon: Users,
      title: '🤝 A Local CPA Team in Your Corner',
      text: 'If you ever need help beyond DIY tools, our KGOB CPAs are here to support you with real, human guidance.'
    }
  ];

  return (
    <section
      className="bg-[#F7F6F2] py-16 lg:py-20"
      data-testid="what-you-get-section"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-4">
            What You Get When You Enter
          </h2>
        </motion.div>

        <div className="space-y-4">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="p-6 border-[#E1DFD7] bg-white hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20">
                      <Icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#0B2240] mb-2">
                        {benefit.title}
                      </h3>
                      <p className="text-[#4C5A70] leading-relaxed">
                        {benefit.text}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
        
        {/* Go Deeper - Subtle Upsell Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8"
        >
          <div className="p-6 border-2 border-dashed border-[#D4AF37]/30 rounded-xl bg-white/50 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm font-semibold text-[#0B2240]">Want deeper guidance?</span>
            </div>
            <p className="text-sm text-[#4C5A70] mb-4 max-w-lg mx-auto">
              The free kit gives you the quick wins. If you want a complete year-end tax strategy with step-by-step guidance, explore the Let's Talk Taxes Holiday Bundle.
            </p>
            <a
              href="https://letstalktaxes.io"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#B99610] font-medium transition-colors group"
              data-testid="holiday-bundle-link"
            >
              View Holiday Bundle
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </motion.div>
        
        {/* Reassurance */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center text-sm text-[#4C5A70] mt-8 max-w-2xl mx-auto"
        >
          We'll never sell your information. You'll get practical tax tips, occasional updates, and you can unsubscribe at any time.
        </motion.p>
      </div>
    </section>
  );
};
